import {
  canUserApproveLeaveRequest,
  canUserApproveAdvanceRequest,
  canUserApproveOvertimeRequest,
  canUserApproveWeeklyRestRequest,
  canUserApprovePermitRequest,
  canUserApproveTimesheetRequest,
  canUserApproveInventoryNight,
} from "./dataMappers";
import { isOpenRequest } from "./requestHelpers";
import {
  isHrFamilyRole,
  canHrApproveRequests,
  isPrimaryHrRole,
} from "./hrStaffHelpers";
import { countPendingWfhApprovals } from "./wfhHelpers";
import { countPendingResignationsForUser } from "./resignHelpers";
import { isAppealSkipLevelReviewer } from "./appealCycle";
import {
  isCrossBranchAppealForUser,
  isOrphanedSameBranchAppeal,
} from "./reportingTree";
import { isGmAppealActionRequired } from "./appealHrDeadline";

const PENDING_APPROVAL_STATUSES = new Set([
  "pending",
  "modified",
  "gm_pending",
]);

const LEAVE_LIKE_SOURCES = new Set(["leave", "weekly-rest", "permit"]);

function buildTeamUserIds(teamMembers) {
  const ids = new Set();
  (teamMembers || []).forEach((emp) => {
    if (emp.userId) ids.add(String(emp.userId));
    if (emp.id) ids.add(String(emp.id));
  });
  return ids;
}

function isTeamRequest(request, teamUserIds) {
  if (!request) return false;
  if (request.userId && teamUserIds.has(String(request.userId))) return true;
  if (request.employeeId && teamUserIds.has(String(request.employeeId))) {
    return true;
  }
  return false;
}

/**
 * Same visibility rules as PendingApprovals (actionable inbox).
 */
export function filterVisiblePendingApprovals(
  pendingRequests,
  { user, teamMembers = [], teamOnly = false, sources = null } = {},
) {
  const allowedSources =
    Array.isArray(sources) && sources.length > 0 ? new Set(sources) : null;
  const teamUserIds = buildTeamUserIds(teamMembers);

  const filtered = (pendingRequests || []).filter((r) => {
    if (!PENDING_APPROVAL_STATUSES.has(r.status)) return false;

    const isOwn =
      (user?.id && r.userId && String(r.userId) === String(user.id)) ||
      (user?.employeeId &&
        r.employeeId &&
        String(r.employeeId) === String(user.employeeId));
    if (isOwn) return false;

    const isRoleScopedApproval =
      r._source === "timesheet" || r._source === "inventory_night";
    if (teamOnly && !isRoleScopedApproval && !isTeamRequest(r, teamUserIds)) {
      return false;
    }
    if (allowedSources && (!r._source || !allowedSources.has(r._source))) {
      return false;
    }

    if (r._source === "leave") {
      return canUserApproveLeaveRequest(r, user);
    }
    if (r._source === "advance") {
      return canUserApproveAdvanceRequest(r, user);
    }
    if (r._source === "overtime") {
      return canUserApproveOvertimeRequest(r, user);
    }
    if (r._source === "weekly-rest") {
      return canUserApproveWeeklyRestRequest(r, user);
    }
    if (r._source === "permit") {
      return canUserApprovePermitRequest(r, user);
    }
    if (r._source === "timesheet") {
      return canUserApproveTimesheetRequest(r, user);
    }
    if (r._source === "inventory_night") {
      return canUserApproveInventoryNight(r, user);
    }

    // شاشة فريق الـ HR: اعرض فقط الطلبات القابلة للموافقة عبر الـ API
    if (teamOnly) return false;

    return true;
  });

  const seen = new Set();
  return filtered.filter((r) => {
    if (!LEAVE_LIKE_SOURCES.has(r._source)) return true;
    const key = [
      r._source || "",
      r.userId || "",
      r.type || "",
      r.startDate || "",
      r.endDate || "",
      r.weeklyRestDay || "",
      r.permitStartTime || "",
      r.permitEndTime || "",
      r.reason || "",
      r.status || "",
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function countVisiblePendingApprovals(
  pendingRequests,
  options = {},
) {
  return filterVisiblePendingApprovals(pendingRequests, options).length;
}

function isOwnRequest(request, user) {
  if (!request || !user) return false;
  if (user.id && request.userId) {
    return String(request.userId) === String(user.id);
  }
  if (user.employeeId && request.employeeId) {
    return String(request.employeeId) === String(user.employeeId);
  }
  const code = user.code;
  return Boolean(code && request.employeeCode && request.employeeCode === code);
}

/** طلباتي / السلف: طلبات الموظف المفتوحة بانتظار الرد */
export function countOwnOpenRequests(
  pendingRequests,
  user,
  sources = null,
) {
  const allowed =
    Array.isArray(sources) && sources.length > 0 ? new Set(sources) : null;
  return (pendingRequests || []).filter((r) => {
    if (!isOwnRequest(r, user) || !isOpenRequest(r)) return false;
    if (allowed && (!r._source || !allowed.has(r._source))) return false;
    return true;
  }).length;
}

function resolveAppealActorLevel(user) {
  const role = user?.role;
  if (canHrApproveRequests(role)) return "hr";
  if (role === "department-manager") return "departmentManager";
  if (user?.backendRoles?.includes("regional_manager")) {
    return "regionalManager";
  }
  if (role === "manager") return "manager";
  return null;
}

function isAppealVisibleToReviewer(appeal, user, employeesByBranch) {
  const role = user?.role;
  const currentEmployeeId = user?.employeeId || null;
  const isRegionalManager = user?.backendRoles?.includes("regional_manager");

  if (isRegionalManager || role === "department-manager") {
    const employee = Object.values(employeesByBranch || {})
      .flat()
      .find((e) => String(e.id) === String(appeal.employeeId));
    return (
      String(employee?.departmentBackendId) ===
        String(user?.departmentBackendId) ||
      String(employee?.departmentId) === String(user?.departmentId) ||
      Boolean(appeal.levels?.manager?.status) ||
      Boolean(appeal.levels?.regionalManager?.status)
    );
  }

  if (role === "manager") {
    const employees = Object.values(employeesByBranch || {}).flat();
    if (isCrossBranchAppealForUser(appeal, user, employees)) return false;
    if (String(appeal.managerId) === String(currentEmployeeId)) return true;
    if (isAppealSkipLevelReviewer(appeal, currentEmployeeId)) return true;
    if (isOrphanedSameBranchAppeal(appeal, user, employees)) return true;
    const employee = employees.find(
      (e) => String(e.id) === String(appeal.employeeId),
    );
    return String(employee?.managerId) === String(currentEmployeeId);
  }

  if (isHrFamilyRole(role) || role === "gm") return true;
  return false;
}

function canRespondToAppeal(appeal, user) {
  const userLevel = resolveAppealActorLevel(user);
  if (!userLevel || !appeal) return false;
  if (
    user?.employeeId &&
    String(appeal.employeeId) === String(user.employeeId)
  ) {
    return false;
  }
  if (
    (isPrimaryHrRole(user?.role) || user?.role === "admin") &&
    !appeal.finalStatus &&
    String(appeal.status || "").includes("pending")
  ) {
    return true;
  }
  const isDirectManager =
    user?.employeeId &&
    String(appeal.managerId) === String(user.employeeId);
  if (
    isDirectManager &&
    appeal.status === "manager_pending" &&
    !appeal.finalStatus
  ) {
    return true;
  }
  if (
    isAppealSkipLevelReviewer(appeal, user?.employeeId) &&
    appeal.status === "regionalManager_pending" &&
    !appeal.finalStatus
  ) {
    return true;
  }
  if (appeal.currentLevel !== userLevel) return false;
  if (appeal.status !== `${userLevel}_pending`) return false;
  return true;
}

/**
 * تظلمات تحتاج إجراء من المراجع، أو تظلمات مفتوحة لصاحبها.
 */
export function countAppealsNeedingAttention(
  appeals,
  user,
  employeesByBranch = {},
) {
  const list = appeals || [];
  const role = user?.role;

  if (role === "employee") {
    return list.filter(
      (a) =>
        (String(a.employeeId) === String(user?.employeeId) ||
          a.employeeCode === user?.code) &&
        a.finalStatus == null &&
        String(a.status || "").includes("pending"),
    ).length;
  }

  if (role === "gm") {
    return list.filter(
      (a) =>
        isGmAppealActionRequired(a) &&
        String(a.employeeId) !== String(user?.employeeId),
    ).length;
  }

  return list.filter(
    (a) =>
      isAppealVisibleToReviewer(a, user, employeesByBranch) &&
      canRespondToAppeal(a, user),
  ).length;
}

/**
 * جزاءات بانتظار اعتماد HR / GM.
 */
export function countPendingHrPenalties(penalties = [], user) {
  const role = user?.role;
  if (!isHrFamilyRole(role) && role !== "gm" && role !== "admin") {
    return 0;
  }
  return (penalties || []).filter((p) => p.status === "pending_hr").length;
}

/**
 * عناصر تحتاج إجراء من المستخدم (موافقات / تظلمات / شكاوى / استقالات / جزاءات).
 * لا يشمل إشعارات القراءة ولا طلبات الموظف المفتوحة عند طرف آخر.
 */
const AWAITING_USER_BADGE_KEYS = [
  "approvals",
  "team-approvals",
  "advance-approvals",
  "appeals",
  "received-complaints",
  "resignations",
  "penalties",
  "wfh",
  "recruitment",
  "evaluations",
];

export function countAwaitingUserActions(badges = {}, role) {
  if (role === "employee") return 0;
  return AWAITING_USER_BADGE_KEYS.reduce((sum, key) => {
    const value = Number(badges[key]) || 0;
    return sum + (value > 0 ? value : 0);
  }, 0);
}

/**
 * يبني كائن badges لمفاتيح nav في SeidBar حسب الدور.
 */
export function buildActionBadges({
  user,
  pendingRequests = [],
  teamMembers = [],
  appeals = [],
  employeesByBranch = {},
  unreadCount = 0,
  unreadAnnouncementCount = 0,
  receivedComplaintsCount = 0,
  resignations = [],
  employeePenalties = [],
  wfhRequests = [],
  recruitmentInbox = {},
  pendingEvaluationCount = 0,
} = {}) {
  const role = user?.role;
  const wfhPending = countPendingWfhApprovals(wfhRequests, user);
  const resignationApprovals = countPendingResignationsForUser(
    resignations,
    user,
  );
  const base = {
    notifications: unreadCount,
    ...(unreadAnnouncementCount > 0
      ? { announcements: unreadAnnouncementCount }
      : {}),
    appeals: countAppealsNeedingAttention(appeals, user, employeesByBranch),
    "received-complaints": receivedComplaintsCount,
    ...(resignationApprovals > 0
      ? { resignations: resignationApprovals }
      : {}),
    penalties: countPendingHrPenalties(employeePenalties, user),
    ...(wfhPending > 0 ? { wfh: wfhPending } : {}),
  };

  const leaveOwn = countOwnOpenRequests(pendingRequests, user, [
    "leave",
    "permit",
    "weekly-rest",
  ]);
  const advanceOwn = countOwnOpenRequests(pendingRequests, user, ["advance"]);
  const overtimeOwn = countOwnOpenRequests(pendingRequests, user, [
    "overtime",
  ]);

  if (leaveOwn > 0) base.leave = leaveOwn;
  if (advanceOwn > 0) base.advance = advanceOwn;
  if (overtimeOwn > 0) base.overtime = overtimeOwn;

  if (role === "manager") {
    base.approvals = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      teamOnly: true,
    });
  } else if (role === "department-manager") {
    base.approvals = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      teamOnly: false,
    });
  } else if (role === "hr" || role === "hr-staff") {
    // Must match PendingApprovals sources for team-approvals.
    // Resignations have their own nav badge and are not listed on this page.
    base["team-approvals"] = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      teamOnly: false,
      sources: ["leave", "weekly-rest", "permit", "overtime"],
    });
    base["advance-approvals"] = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      teamOnly: false,
      sources: ["advance"],
    });
  } else if (role === "gm") {
    const leaveLike = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      sources: ["leave", "permit", "overtime"],
    });
    const advances = countVisiblePendingApprovals(pendingRequests, {
      user,
      teamMembers,
      sources: ["advance"],
    });
    base.approvals = leaveLike + advances;
    base.requests = (pendingRequests || []).filter((r) =>
      PENDING_APPROVAL_STATUSES.has(r.status),
    ).length;
  } else if (role === "owner") {
    base.requests = (pendingRequests || []).filter((r) =>
      PENDING_APPROVAL_STATUSES.has(r.status),
    ).length;
  }

  const recruitmentCount =
    (Number(recruitmentInbox.requisitions) || 0) +
    (Number(recruitmentInbox.offers) || 0) +
    (Number(recruitmentInbox.interviews) || 0);
  if (recruitmentCount > 0) {
    base.recruitment = recruitmentCount;
  }

  const evaluationCount = Number(pendingEvaluationCount) || 0;
  if (evaluationCount > 0) {
    base.evaluations = evaluationCount;
  }

  return base;
}
